/**
 * LWI-1563 — Logikor design tokens (source of truth).
 *
 * Every value traces to docs/design/AUDIT.md. This file has no imports so
 * `src/design-system/` can be copied into another app, or moved into a
 * shared package, as-is. `npm run tokens:css` regenerates tokens.css from it.
 *
 * Values MUI derives itself (primary.dark/light, semantic defaults) are
 * recorded exactly as MUI v6 computes them, so the CSS variables match the
 * MUI theme.
 */

// Brand blue scale (was `customBlue` in src/theme/variants.js).
export const brand = {
  50: "#e9f0fb",
  100: "#c8daf4",
  200: "#a3c1ed",
  300: "#7ea8e5",
  400: "#6395e0",
  500: "#4782da",
  600: "#407ad6",
  700: "#376fd0",
  800: "#2f65cb",
  900: "#2052c2",
};

// MUI grey scale.
export const neutral = {
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#eeeeee",
  300: "#e0e0e0",
  400: "#bdbdbd",
  500: "#9e9e9e",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
};

export const common = {
  white: "#ffffff",
  black: "#000000",
};

// Red of the Logikor mark. Logos, the login backdrop and the sidebar's
// active-item marker (LWI-1592) only; never content-area UI state, where red
// means error.
export const logikorRed = "#c01e24";

// Per-mode colors. Each mode has the same keys.
export const color = {
  light: {
    primary: {
      main: brand[700],
      light: "#5f8bd9",
      dark: "#264d91",
      contrastText: common.white,
    },
    secondary: {
      main: brand[500],
      light: "#6b9be1",
      dark: "#315b98",
      contrastText: common.white,
    },
    surface: {
      default: "#f7f9fc",
      paper: common.white,
    },
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },
    divider: "rgba(0, 0, 0, 0.12)",
    action: {
      hover: "rgba(0, 0, 0, 0.04)",
    },
    success: { main: "#2e7d32", light: "#4caf50", dark: "#1b5e20" },
    error: { main: "#d32f2f", light: "#ef5350", dark: "#c62828" },
    warning: { main: "#ed6c02", light: "#ff9800", dark: "#e65100" },
    info: { main: "#0288d1", light: "#03a9f4", dark: "#01579b" },
    callout: {
      infoSurface: "#e3f2fd",
      infoBorder: "#90caf9",
      warningSurface: "#fff3cd",
      errorSurface: "#fff3f3",
      errorBorder: "#ffcdd2",
    },
    overlay: {
      scrim: "rgba(0, 0, 0, 0.55)",
    },
    sidebar: {
      background: "#232f3e",
      active: "#1a232e", // sidebar.background darkened 5% (polished darken(0.05))
      footer: "#1e2a38",
      text: neutral[200],
      badge: brand[500],
      // LWI-1592 — active nav item: 4px brand-red bar + white label. The red
      // is only 2.2:1 on the navy, so the label carries the state.
      activeMarker: logikorRed,
      activeText: common.white,
    },
    header: {
      background: common.white,
      text: neutral[500],
      search: neutral[800],
      indicator: brand[600],
    },
    footer: {
      background: common.white,
      text: neutral[500],
    },
  },
  dark: {
    primary: {
      main: brand[600],
      light: "#6694de",
      dark: "#2c5595",
      contrastText: common.white,
    },
    secondary: {
      main: brand[500],
      light: "#6b9be1",
      dark: "#315b98",
      contrastText: common.white,
    },
    surface: {
      default: "#1b2635",
      paper: "#233044",
    },
    text: {
      primary: "rgba(255, 255, 255, 0.95)",
      secondary: "rgba(255, 255, 255, 0.5)",
      disabled: "rgba(255, 255, 255, 0.5)",
    },
    divider: "rgba(255, 255, 255, 0.12)",
    action: {
      hover: "rgba(255, 255, 255, 0.08)",
    },
    success: { main: "#66bb6a", light: "#81c784", dark: "#388e3c" },
    error: { main: "#f44336", light: "#e57373", dark: "#d32f2f" },
    warning: { main: "#ffa726", light: "#ffb74d", dark: "#f57c00" },
    info: { main: "#29b6f6", light: "#4fc3f7", dark: "#0288d1" },
    // LWI-1567 — dark semantic main blended onto dark paper (#233044):
    // surfaces at 12%, borders at 40%.
    callout: {
      infoSurface: "#244059",
      infoBorder: "#25668b",
      warningSurface: "#3d3e40",
      errorSurface: "#3c3242",
      errorBorder: "#77383e",
    },
    overlay: {
      scrim: "rgba(0, 0, 0, 0.55)",
    },
    sidebar: {
      background: "#232f3e",
      active: "#1a232e",
      footer: "#1e2a38",
      text: neutral[200],
      badge: brand[500],
      // LWI-1592 — active nav item: 4px brand-red bar + white label. The red
      // is only 2.2:1 on the navy, so the label carries the state.
      activeMarker: logikorRed,
      activeText: common.white,
    },
    header: {
      background: "#1b2635",
      text: neutral[300],
      search: neutral[200],
      indicator: brand[600],
    },
    footer: {
      background: "#233044",
      text: neutral[300],
    },
  },
};

// LWI-1117 chart palette. Assign categorical slots by position; never cycle.
export const dataviz = {
  light: {
    categorical: [
      "#2a78d6",
      "#eb6834",
      "#1baf7a",
      "#eda100",
      "#e87ba4",
      "#008300",
      "#4a3aa7",
      "#e34948",
    ],
    ordinal: ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab"],
  },
  dark: {
    categorical: [
      "#3987e5",
      "#d95926",
      "#199e70",
      "#c98500",
      "#d55181",
      "#008300",
      "#9085e9",
      "#e66767",
    ],
    ordinal: ["#6da7ec", "#3987e5", "#256abf", "#184f95"],
  },
};

export const fontFamily = {
  sans: [
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(","),
  mono: "monospace",
};

// Inter is loaded at 300–600 only; 700 renders as 600 (audit Q7).
export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
};

// Type scales (audit §2.2, §2.4, §2.5; LWI-1567). Plural names so they
// never collide with the CSS property names or local variables.
export const fontSizes = {
  xs: "11px",
  sm: "12px",
  md: "13px",
  base: "14px",
  lg: "16px",
  h5: "17px",
  xl: "18px",
  "2xl": "20px",
  "3xl": "24px",
  "4xl": "28px",
  "5xl": "32px",
};

export const lineHeights = {
  none: 1,
  tight: 1.25,
  normal: 1.5,
  xs: "16px",
  sm: "20px",
  md: "22px",
  lg: "24px",
  xl: "28px",
};

// Tracking from the Logikor type ramp, named for the style each pairs with.
export const letterSpacings = {
  body1: "0.0787px",
  body2: "0.1px",
  subtitle2: "0.07px",
  button: "0.3px",
  caption: "0.4px",
  title: "0.0412px",
  overline: "0.08em",
};

// MUI base font size (px). MUI scales its own default variants by 13/14.
export const baseFontSize = 13;

// Text styles, values exactly as the theme ships them.
export const textStyles = {
  h1: { fontSize: "2rem", fontWeight: 600, lineHeight: lineHeights.tight },
  h2: { fontSize: "1.75rem", fontWeight: 600, lineHeight: lineHeights.tight },
  h3: { fontSize: "1.5rem", fontWeight: 600, lineHeight: lineHeights.tight },
  h4: { fontSize: fontSizes["3xl"], fontWeight: 600, lineHeight: lineHeights.xl },
  h5: { fontSize: "1.0625rem", fontWeight: 500, lineHeight: lineHeights.tight },
  h6: { fontSize: "1rem", fontWeight: 500, lineHeight: lineHeights.tight },
  subtitle2: {
    fontSize: fontSizes.base,
    fontWeight: 600,
    lineHeight: lineHeights.sm,
    letterSpacing: letterSpacings.subtitle2,
  },
  body1: {
    fontSize: fontSizes.base,
    fontWeight: 400,
    lineHeight: lineHeights.md,
    letterSpacing: letterSpacings.body1,
  },
  body2: {
    fontSize: fontSizes.base,
    fontWeight: 400,
    lineHeight: lineHeights.sm,
    letterSpacing: letterSpacings.body2,
  },
  button: {
    fontSize: fontSizes.base,
    fontWeight: 600,
    lineHeight: lineHeights.lg,
    letterSpacing: letterSpacings.button,
    textTransform: "none",
  },
  // Repeated sx blocks with no MUI variant yet (audit §2.6).
  title: { fontSize: fontSizes["2xl"], lineHeight: lineHeights.lg, letterSpacing: letterSpacings.title },
  label: {
    fontSize: fontSizes.sm,
    fontWeight: 400,
    lineHeight: lineHeights.xs,
    letterSpacing: letterSpacings.caption,
  },
};

// Spacing. MUI unit is 8px; names are px / 4 (`space["4"]` = 16px = spacing(2)).
export const spacingUnit = 8;
export const space = {
  0: "0px",
  "0_5": "2px",
  1: "4px",
  "1_5": "6px",
  2: "8px",
  "2_5": "10px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px",
  14: "56px",
  16: "64px",
  20: "80px",
  24: "96px",
};

export const radius = {
  none: "0px",
  xs: "2px",
  sm: "4px", // MUI shape.borderRadius (default)
  md: "6px", // cards, chips
  lg: "8px",
  xl: "12px",
  "2xl": "20px",
  pill: "999px",
  circle: "50%",
};

export const shadow = {
  none: "none",
  xs: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)", // every MUI elevation (audit Q6)
  card: "rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px -1px",
  panel: "0px 0px 14px 0px rgba(53, 64, 82, 0.05)",
  popover: "0px 2px 8px rgba(0, 0, 0, 0.15)",
  stickyEdge: "2px 0 4px rgba(0, 0, 0, 0.1)",
  ring: "0 0 0 1px rgba(0, 0, 0, 0.15)",
  text: "0 1px 1px rgba(0, 0, 0, 0.2)", // text-shadow on filled chips
};

export const opacity = {
  hover: 0.04,
  selected: 0.08,
  focus: 0.12,
};

export const iconSize = {
  xs: "16px",
  sm: "18px",
  md: "20px",
};

// Custom layers, plus MUI's scale so non-MUI apps stack the same way.
export const zIndex = {
  base: 0,
  raised: 1,
  sticky: 10,
  mobileStepper: 1000,
  fab: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// Custom breakpoints (MUI defaults are md 900, lg 1200, xl 1536).
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1440,
};

const tokens = {
  brand,
  neutral,
  common,
  logikorRed,
  color,
  dataviz,
  fontFamily,
  fontWeight,
  fontSizes,
  lineHeights,
  letterSpacings,
  baseFontSize,
  textStyles,
  spacingUnit,
  space,
  radius,
  shadow,
  opacity,
  iconSize,
  zIndex,
  breakpoints,
};

export default tokens;
